import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { BILLING } from "@/constants/testIds";
import { Check, Shield, Sparkles, Users, Zap } from "lucide-react";
import { ONE_TIME_PLANS, cancelledWithAccess, pastDue } from "@/lib/subscription";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [cycle, setCycle] = useState("monthly"); // monthly | annual
  const [selected, setSelected] = useState(null); // plan key
  const [loading, setLoading] = useState(false);
  const [paymentsReady, setPaymentsReady] = useState(false);
  const cardRef = useRef(null);
  const cardInstance = useRef(null);

  useEffect(() => {
    (async () => {
      const r = await api.get("/billing/config");
      setConfig(r.data);
      if (user?.is_coach) setSelected("coach_starter_monthly");
      else setSelected("monthly");
    })();
  }, [user?.is_coach]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!config || !window.Square || cardInstance.current) return;
      try {
        const payments = window.Square.payments(config.square_app_id, config.square_location_id);
        const card = await payments.card();
        await card.attach(cardRef.current);
        if (cancelled) return;
        cardInstance.current = card;
        setPaymentsReady(true);
      } catch (e) {
        console.error(e);
        toast.error("Payment form failed to load");
      }
    };
    const timer = setInterval(() => {
      if (window.Square) {
        clearInterval(timer);
        init();
      }
    }, 200);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [config]);

  const subscribe = async () => {
    if (!cardInstance.current) return toast.error("Card form not ready");
    if (!selected) return toast.error("Pick a plan");
    setLoading(true);
    try {
      const result = await cardInstance.current.tokenize();
      if (result.status !== "OK") {
        toast.error("Card validation failed");
        setLoading(false);
        return;
      }
      await api.post("/billing/checkout", { plan: selected, source_id: result.token });
      await refresh();
      toast.success("Subscription active");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  const startTrial = async () => {
    if (!cardInstance.current) return toast.error("Card form not ready");
    if (!selected) return toast.error("Pick a plan");
    setLoading(true);
    try {
      const result = await cardInstance.current.tokenize();
      if (result.status !== "OK") {
        toast.error("Card validation failed");
        setLoading(false);
        return;
      }
      await api.post("/billing/start-trial", { plan: selected, source_id: result.token });
      await refresh();
      toast.success("7-day free trial started — cancel anytime before day 7");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trial start failed");
    } finally {
      setLoading(false);
    }
  };

  const updateCard = async () => {
    if (!cardInstance.current) return toast.error("Card form not ready");
    setLoading(true);
    try {
      const result = await cardInstance.current.tokenize();
      if (result.status !== "OK") {
        toast.error("Card validation failed");
        setLoading(false);
        return;
      }
      const r = await api.post("/billing/update-card", { source_id: result.token });
      await refresh();
      toast.success(r?.data?.detail || "Card updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Card update failed");
    } finally {
      setLoading(false);
    }
  };

  const cancelSub = async () => {
    const endDate = user?.subscription?.expires_at
      ? new Date(user.subscription.expires_at).toLocaleDateString()
      : "the end of your billing period";
    const isTrialSub = user?.subscription?.status === "trial";
    const msg = isTrialSub
      ? `Cancel your free trial?\n\nYour card will NOT be charged and you'll keep access until ${endDate}.\n\nNo refunds are issued for cancellations.`
      : `Cancel your subscription?\n\nYou'll keep full access until ${endDate}, then your subscription ends. Your card won't be charged again.\n\nNo refunds are issued for cancellations.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post("/billing/cancel");
      await refresh();
      const until = r?.data?.access_until
        ? new Date(r.data.access_until).toLocaleDateString()
        : endDate;
      toast.success(`Cancelled — access continues until ${until}. No further charges.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const isPastDue = pastDue(user);
  const hasActiveSub =
    (user?.subscription?.status === "active" && !isPastDue) ||
    user?.subscription?.status === "trial" ||
    user?.subscription?.status === "trialing";
  // One-time prepaid packages never auto-renew — nothing to cancel.
  const isOneTimePlan = ONE_TIME_PLANS.includes(user?.subscription?.plan);
  const isCancelledWithAccess = cancelledWithAccess(user);
  // Trial is consumed only when the user has used the trial AND is no longer
  // in an active sub. Mid-trial users (status='trial'|'trialing') are caught
  // by hasActiveSub above, so they get the Cancel-trial CTA instead.
  const trialUsed =
    !hasActiveSub &&
    !isCancelledWithAccess &&
    (user?.subscription?.status === "expired" ||
      user?.subscription?.status === "cancelled" ||
      Boolean(user?.trial_ends_at));

  const isCoach = user?.is_coach;
  const trialDaysLeft = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Build the tile list
  const coachTiles = (config?.coach_tiers || []).map((t) => ({
    key: t.key,
    label: t.label,
    plan: cycle === "monthly" ? t.monthly_plan : t.annual_plan,
    price: (cycle === "monthly" ? t.monthly_cents : t.annual_cents) / 100,
    cycleLabel: cycle === "monthly" ? "/mo" : "/yr",
    limit: t.client_limit,
  }));
  const clientTiles = config
    ? [
        { key: "free", label: "Free", plan: null, price: 0, cycleLabel: "forever" },
        { key: "monthly", label: "Premium · Monthly", plan: "monthly", price: config.monthly_price_cents / 100, cycleLabel: "/mo" },
        { key: "annual", label: "Premium · Annual", plan: "annual", price: config.annual_price_cents / 100, cycleLabel: "/yr" },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-blaze">/ Pricing</div>
        <h1 className="font-heading text-5xl uppercase mt-1">
          {isCoach ? "Pick your roster size." : "Unlock unlimited."}
        </h1>
        {isCoach && trialDaysLeft > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blaze/10 border border-blaze/40 rounded-sm text-blaze text-xs uppercase tracking-widest" data-testid="trial-banner">
            <Zap className="h-3 w-3" /> {trialDaysLeft} days free trial remaining
          </div>
        )}
        {user?.subscription?.status === "active" && (
          <p className="text-zinc-300 mt-2">
            You're <span className="text-blaze font-bold uppercase">{user.subscription.plan}</span>{" "}
            · expires {new Date(user.subscription.expires_at).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Cycle toggle (coach only) */}
      {isCoach && (
        <div className="inline-flex border border-obsidian-600 rounded-sm overflow-hidden">
          <button data-testid={BILLING.monthly} onClick={() => setCycle("monthly")} className={`px-4 py-2 text-xs uppercase tracking-widest ${cycle === "monthly" ? "bg-blaze text-white" : "text-zinc-400 hover:text-white"}`}>Monthly</button>
          <button data-testid={BILLING.annual} onClick={() => setCycle("annual")} className={`px-4 py-2 text-xs uppercase tracking-widest ${cycle === "annual" ? "bg-blaze text-white" : "text-zinc-400 hover:text-white"}`}>Annual · save 20%</button>
        </div>
      )}

      {/* Tiles */}
      <div className={`grid gap-4 ${isCoach ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-3"}`}>
        {(isCoach ? coachTiles : clientTiles).map((t) => (
          <button
            key={t.key}
            data-testid={`plan-${t.key}`}
            type="button"
            disabled={t.plan === null}
            onClick={() => t.plan && setSelected(t.plan)}
            className={`text-left obs-card transition-colors ${
              selected === t.plan ? "!border-blaze ring-1 ring-blaze" : "hover:border-blaze/60"
            } ${t.plan === null ? "opacity-70 cursor-default" : ""}`}
          >
            <div className="overline">{t.label}</div>
            <h3 className="font-heading text-4xl uppercase mt-1">
              ${t.price.toFixed(t.price < 10 ? 2 : 0)}
            </h3>
            <p className="text-xs text-zinc-500 uppercase tracking-widest mb-3">{t.cycleLabel}</p>
            {isCoach ? (
              <ul className="space-y-1.5 text-xs text-zinc-300">
                <li className="flex gap-2"><Users className="h-3 w-3 text-blaze" /> Up to {t.limit >= 99999 ? "Unlimited" : t.limit} clients</li>
                <li className="flex gap-2"><Sparkles className="h-3 w-3 text-blaze" /> Program builder + AI drafts</li>
                <li className="flex gap-2"><Sparkles className="h-3 w-3 text-blaze" /> Client notes + check-ins</li>
                <li className="flex gap-2"><Check className="h-3 w-3 text-blaze" /> 7-day free trial (card required, cancel anytime)</li>
              </ul>
            ) : (
              <ul className="space-y-1.5 text-sm text-zinc-300">
                {t.key === "free" ? (
                  <>
                    <li className="flex gap-2"><Check className="h-4 w-4 text-blaze" /> Macro calculator</li>
                    <li className="flex gap-2"><Check className="h-4 w-4 text-blaze" /> Workout tracking</li>
                    <li className="flex gap-2"><Check className="h-4 w-4 text-blaze" /> Progress charts</li>
                    <li className="flex gap-2"><Check className="h-4 w-4 text-blaze" /> 10 coach messages</li>
                  </>
                ) : (
                  <>
                    <li className="flex gap-2"><Sparkles className="h-4 w-4 text-blaze" /> Unlimited AI coach</li>
                    <li className="flex gap-2"><Sparkles className="h-4 w-4 text-blaze" /> Unlimited meal plans</li>
                    <li className="flex gap-2"><Sparkles className="h-4 w-4 text-blaze" /> AI progression</li>
                    <li className="flex gap-2"><Sparkles className="h-4 w-4 text-blaze" /> Advanced analytics</li>
                  </>
                )}
              </ul>
            )}
          </button>
        ))}
      </div>

      {/* Payment */}
      <div className="obs-card max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Secure checkout · Square</div>
            <div className="font-heading text-2xl uppercase">Pay with card</div>
          </div>
        </div>
        <div
          ref={cardRef}
          data-testid={BILLING.cardContainer}
          className="min-h-[60px] bg-obsidian-900 border border-obsidian-600 rounded-sm p-3 mb-4"
        />
        {isPastDue ? (
          <>
            <p className="text-sm text-red-400 mb-3" data-testid="past-due-note">
              Your renewal charge failed, so access is paused. Enter your card
              above and pay now — access is restored instantly and auto-renewal
              resumes.
            </p>
            <button
              data-testid="pay-restore-access"
              onClick={updateCard}
              disabled={!paymentsReady || loading}
              className="blaze-btn w-full py-3 text-sm disabled:opacity-50"
            >
              {loading ? "Processing…" : "Pay now & restore access"}
            </button>
          </>
        ) : hasActiveSub ? (
          isOneTimePlan ? (
            <p className="text-sm text-zinc-400" data-testid="prepaid-package-note">
              Prepaid package — never auto-renews. Access ends{" "}
              {user?.subscription?.expires_at
                ? new Date(user.subscription.expires_at).toLocaleDateString()
                : "when the package expires"}
              . Nothing to cancel.
            </p>
          ) : (
            <button
              data-testid="cancel-subscription"
              onClick={cancelSub}
              className="ghost-btn w-full py-3 text-sm"
            >
              Cancel{user?.subscription?.status === "trial" ? " free trial" : " subscription"}
            </button>
          )
        ) : isCancelledWithAccess ? (
          <>
            <p className="text-sm text-amber-400/90 mb-3" data-testid="cancelled-access-note">
              Subscription cancelled — you keep full access until{" "}
              {new Date(user.subscription.expires_at).toLocaleDateString()}. No further charges.
              Changed your mind? Re-subscribe below.
            </p>
            <button
              data-testid={BILLING.subscribe}
              onClick={subscribe}
              disabled={!paymentsReady || loading || !selected}
              className="blaze-btn w-full py-3 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {loading ? "Processing…" : `Re-subscribe ${selected || ""}`}
            </button>
          </>
        ) : trialUsed ? (
          // Trial already consumed (expired or cancelled). Only Subscribe is offered.
          <>
            <p className="text-xs text-zinc-400 mb-3 uppercase tracking-widest">
              Your free trial has ended. Pick a plan and add a card to continue.
            </p>
            <button
              data-testid={BILLING.subscribe}
              onClick={subscribe}
              disabled={!paymentsReady || loading || !selected}
              className="blaze-btn w-full py-3 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {loading ? "Processing…" : `Subscribe ${selected || ""}`}
            </button>
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              data-testid="start-trial"
              onClick={startTrial}
              disabled={!paymentsReady || loading || !selected}
              className="blaze-btn w-full py-3 text-sm disabled:opacity-50"
            >
              {loading ? "Starting…" : "Start 7-day free trial"}
            </button>
            <button
              data-testid={BILLING.subscribe}
              onClick={subscribe}
              disabled={!paymentsReady || loading || !selected}
              className="ghost-btn w-full py-3 text-sm disabled:opacity-50"
            >
              {loading ? "Processing…" : `Subscribe now ${selected || ""}`}
            </button>
          </div>
        )}
        <p className="text-[11px] text-zinc-500 mt-3 uppercase tracking-widest">
          Powered by Square · Cancel anytime — access continues to end of billing period · No refunds
        </p>
      </div>
    </div>
  );
}
